import { type ReactNode } from 'react';
import {
  ButtonIcon,
  Cell,
  CellText,
  Flex,
  SearchField,
  Table,
  Text,
  useTable,
  type ColumnConfig,
  type TableItem,
} from '@backstage/ui';
import type { ScaffolderTaskStatus } from '@backstage/plugin-scaffolder-common';
import { ScaffolderTask } from './hooks/use-task-explorer';
import { STATUS_COLORS, formatDate } from './task-explorer-utils';
import { RiPushpin2Fill, RiPushpinLine } from '@remixicon/react';

type TaskTableItem = TableItem & {
  task: ScaffolderTask;
  templateTitle: string;
  templateName: string;
  userName: string;
  status: ScaffolderTaskStatus;
  createdAt: string;
};

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

type TaskExplorerTableProps = {
  tasks: ScaffolderTask[];
  isSaved: (taskId: string) => boolean;
  onToggleSave: (task: ScaffolderTask) => void;
};

type ActionsCellProps = {
  item: TaskTableItem;
  isSaved: (taskId: string) => boolean;
  onToggleSave: (task: ScaffolderTask) => void;
};

const ActionsCell = ({ item, isSaved, onToggleSave }: ActionsCellProps) => (
  <Cell>
    <ButtonIcon
      variant="secondary"
      aria-label={isSaved(item.id as string) ? 'Unpin' : 'Pin for later'}
      onPress={() => onToggleSave(item.task)}
      icon={isSaved(item.id as string) ? <RiPushpin2Fill /> : <RiPushpinLine />}
    />
  </Cell>
);

const StatusCell = ({ item }: { item: TaskTableItem }) => (
  <Cell>
    <Text
      variant="body-small"
      weight="bold"
      color={STATUS_COLORS[item.status] ?? 'secondary'}
    >
      ● {item.status}
    </Text>
  </Cell>
);

const emptyState: ReactNode = (
  <Text>No tasks found matching your filters.</Text>
);

const extractTemplateName = (
  task: ScaffolderTask,
): {
  title: string;
  name: string;
} => {
  const info = task.spec?.templateInfo;
  const meta = info?.entity?.metadata;
  const name = meta?.name ?? info?.entityRef?.split('/').at(-1) ?? 'Unknown';
  const title = meta?.title ?? name;
  return { title, name };
};

const extractUserName = (createdBy?: string): string => {
  if (!createdBy) return 'Unknown';
  const parts = createdBy.split('/');
  return parts.at(-1) ?? createdBy;
};

const toTableItems = (tasks: ScaffolderTask[]): TaskTableItem[] => {
  return tasks.map(task => {
    const { title, name } = extractTemplateName(task);
    return {
      id: task.id,
      task,
      templateTitle: title,
      templateName: name,
      userName: extractUserName(task.createdBy),
      status: task.status,
      createdAt: task.createdAt,
    };
  });
};

const TemplateCell = ({ item }: { item: TaskTableItem }) => (
  <CellText title={item.templateTitle} href={`/create/tasks/${item.id}`} />
);

const UserCell = ({ item }: { item: TaskTableItem }) => (
  <CellText title={item.userName} />
);

const CreatedAtCell = ({ item }: { item: TaskTableItem }) => (
  <CellText title={formatDate(item.createdAt)} />
);

const buildColumns = (
  isSaved: (taskId: string) => boolean,
  onToggleSave: (task: ScaffolderTask) => void,
): ColumnConfig<TaskTableItem>[] => [
  {
    id: 'template',
    label: 'Template',
    isRowHeader: true,
    isSortable: true,
    defaultWidth: '30fr',
    cell: item => <TemplateCell item={item} />,
  },
  {
    id: 'user',
    label: 'User',
    isSortable: true,
    defaultWidth: '20fr',
    cell: item => <UserCell item={item} />,
  },
  {
    id: 'status',
    label: 'Status',
    isSortable: true,
    defaultWidth: '12fr',
    cell: item => <StatusCell item={item} />,
  },
  {
    id: 'createdAt',
    label: 'Date',
    isSortable: true,
    defaultWidth: '20fr',
    cell: item => <CreatedAtCell item={item} />,
  },
  {
    id: 'actions',
    label: 'Actions',
    defaultWidth: '10fr',
    cell: item => (
      <ActionsCell item={item} isSaved={isSaved} onToggleSave={onToggleSave} />
    ),
  },
];

export const TaskExplorerTable = ({
  tasks,
  isSaved,
  onToggleSave,
}: TaskExplorerTableProps) => {
  const columns = buildColumns(isSaved, onToggleSave);
  const data = toTableItems(tasks);

  const { tableProps, search } = useTable({
    mode: 'complete',
    data,
    initialSort: { column: 'createdAt', direction: 'descending' },
    searchFn: (items, query) => {
      const lowerQuery = query.toLowerCase();
      return items.filter(
        item =>
          item.templateTitle.toLowerCase().includes(lowerQuery) ||
          item.userName.toLowerCase().includes(lowerQuery) ||
          item.status.toLowerCase().includes(lowerQuery) ||
          item.createdAt.toLowerCase().includes(lowerQuery),
      );
    },
    sortFn: (items, sort) => {
      if (!sort?.column || !sort?.direction) return items;
      const direction = sort.direction === 'ascending' ? 1 : -1;
      const columnToField: Record<string, keyof TaskTableItem> = {
        template: 'templateTitle',
        user: 'userName',
        status: 'status',
        createdAt: 'createdAt',
      };
      const field = columnToField[sort.column];
      if (!field) return items;

      return [...items].sort((left, right) => {
        const leftVal = left[field];
        const rightVal = right[field];
        if (typeof leftVal !== 'string' || typeof rightVal !== 'string')
          return 0;
        return leftVal.localeCompare(rightVal) * direction;
      });
    },
    paginationOptions: {
      type: 'page',
      pageSize: PAGE_SIZE_OPTIONS[1],
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      showPageSizeOptions: true,
    },
  });

  const filteredCount =
    (tableProps as { pagination?: { totalCount?: number } }).pagination
      ?.totalCount ?? data.length;
  const totalCount = data.length;

  return (
    <Flex direction="column">
      <Flex justify="between" align="center">
        <Text variant="title-x-small">
          Results (
          {filteredCount === totalCount
            ? totalCount
            : `${filteredCount} of ${totalCount}`}
          )
        </Text>
        <SearchField
          aria-label="Search"
          placeholder="Search..."
          {...search}
          style={{ maxWidth: '256px' }}
        />
      </Flex>
      <Table columnConfig={columns} emptyState={emptyState} {...tableProps} />
    </Flex>
  );
};
